import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { applyAccessibilityPrefs, createPreferenceSaveQueue, DEFAULT_ACCESSIBILITY_PREFS } from '@/lib/accessibility';
import { stopAppAudio } from '@/lib/speech';

const AccessibilityContext = createContext(null);
const fromApi = (row = {}) => ({
  contrastMode: row.contrast_mode, textSize: row.text_size, fontWeight: row.font_weight,
  reduceMotion: row.reduce_motion, textOnly: row.text_only, audioEnabled: row.audio_enabled,
  preferredVoice: row.preferred_voice, speechRate: row.speech_rate,
  onboardingCompleted: row.onboarding_completed, onboardingSkipped: row.onboarding_skipped,
});
const toApi = (prefs) => ({
  contrast_mode: prefs.contrastMode, text_size: prefs.textSize, font_weight: prefs.fontWeight,
  reduce_motion: prefs.reduceMotion, text_only: prefs.textOnly, audio_enabled: prefs.audioEnabled,
  preferred_voice: prefs.preferredVoice || null, speech_rate: prefs.speechRate,
  onboarding_completed: prefs.onboardingCompleted, onboarding_skipped: prefs.onboardingSkipped,
});

export function AccessibilityProvider({ children }) {
  const { user, isAuthenticated, isLoadingAuth } = useAuth();
  const [prefs, setPrefs] = useState(DEFAULT_ACCESSIBILITY_PREFS);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [setupOpen, setSetupOpen] = useState(false);
  const generation = useRef(0);
  const userRef = useRef(user?.id);
  const prefsRef = useRef(DEFAULT_ACCESSIBILITY_PREFS);
  const saveQueue = useRef(createPreferenceSaveQueue((payload) => base44.entities.UserPreference.create(payload)));
  userRef.current = user?.id;

  useEffect(() => {
    const current = ++generation.current;
    prefsRef.current = DEFAULT_ACCESSIBILITY_PREFS; setPrefs(DEFAULT_ACCESSIBILITY_PREFS); applyAccessibilityPrefs(DEFAULT_ACCESSIBILITY_PREFS);
    setReady(false); setSaving(false); setError(''); setSetupOpen(false); stopAppAudio();
    if (isLoadingAuth || !isAuthenticated || !user?.id) return;
    base44.entities.UserPreference.list().then((rows) => {
      if (current !== generation.current) return;
      const loaded = { ...DEFAULT_ACCESSIBILITY_PREFS, ...(rows[0] ? fromApi(rows[0]) : {}) };
      prefsRef.current = loaded; setPrefs(loaded); setSetupOpen(!loaded.onboardingCompleted && !loaded.onboardingSkipped); setReady(true);
    }).catch((e) => { if (current === generation.current) { setError(e.message || 'Could not load accessibility settings'); setReady(true); } });
    return () => { generation.current += 1; };
  }, [isAuthenticated, isLoadingAuth, user?.id]);

  useEffect(() => { applyAccessibilityPrefs(prefs); }, [prefs]);

  async function updatePrefs(changes) {
    const owner = user?.id;
    const ownerGeneration = generation.current;
    if (!owner) return false;
    const next = { ...prefsRef.current, ...changes };
    if (prefsRef.current.audioEnabled && changes.audioEnabled === false) stopAppAudio();
    prefsRef.current = next; setPrefs(next); setSaving(true); setError('');
    const request = saveQueue.current.enqueue(owner, ownerGeneration, toApi(next), (expectedOwner, expectedGeneration) => expectedOwner === userRef.current && expectedGeneration === generation.current);
    try {
      const result = await request;
      if (result?.stale) return false;
      return owner === userRef.current;
    } catch (e) {
      if (owner === userRef.current) setError(e.message || 'Could not save accessibility settings');
      return false;
    } finally { if (owner === userRef.current && ownerGeneration === generation.current) setSaving(false); }
  }

  return <AccessibilityContext.Provider value={{ prefs, updatePrefs, ready, saving, error, setupOpen, setSetupOpen }}>{children}</AccessibilityContext.Provider>;
}

export function useAccessibility() { return useContext(AccessibilityContext); }
